import { Method, Request, sendRequest } from "@myloc/myloc-utils";
import { useCallback, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { AppDispatch } from "../../../app/store";
import { api } from "../../../config/settings";
import { useTranslate } from "../../../language/i18n";
import { ValueOf } from "../../../utils/dataTypes";
import HttpStatusCodes from "../../../utils/HttpStatusCodes";
import { OverloadedReturnType } from "../../dataTypes";
import { setError } from "../../dialog/dialogSlice";
import defaultRestOptions from "../../utils/defaultRestOptions";
import { getValidationResult } from "../../utils/getValidationResults";
import { useValidateUserIsLoggedIn } from "../../utils/useValidateUserIsLoggedIn";
import { UpdatePasswordRequest } from "../dataTypes";

async function sendUpdatePassword(dispatch: AppDispatch, data: UpdatePasswordRequest) {
  const url = api.password.password();
  const request = new Request(url, Method.PUT);

  return await sendRequest(request, data, await defaultRestOptions({ dispatch }));
}

const useUpdatePassword = () => {
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const result = useValidateUserIsLoggedIn();

  const _validateUpdatePassword = useCallback(
    (
      additionalValidation: (request: UpdatePasswordRequest) => OverloadedReturnType<typeof getValidationResult>,
      request?: Partial<UpdatePasswordRequest>,
    ) => {
      if (!request) return getValidationResult(true, translate("DATA_MISSING"), HttpStatusCodes.BAD_REQUEST);
      if (!request.password)
        return getValidationResult(true, translate("PASSWORD_MISSING"), HttpStatusCodes.BAD_REQUEST);
      if (!request.passwordRepeat)
        return getValidationResult(true, translate("PASSWORD_REPEAT_MISSING"), HttpStatusCodes.BAD_REQUEST);
      if (request.password !== request.passwordRepeat)
        return getValidationResult(true, translate("PASSWORDS_DOESNT_MATCH"), HttpStatusCodes.BAD_REQUEST);
      return additionalValidation(request as UpdatePasswordRequest);
    },
    [translate],
  );

  const validateUpdatePasswordWithToken = useCallback(
    (request: Partial<UpdatePasswordRequest>) => {
      if (!request.token) return getValidationResult(true, translate("TOKEN_MISSING"), HttpStatusCodes.BAD_REQUEST);

      return getValidationResult(false);
    },
    [translate],
  );

  const validateUpdatePassword = useCallback(
    (request: Partial<UpdatePasswordRequest>) => {
      if (result.isError) return result;
      if (!request.previous)
        return getValidationResult(true, translate("PREVIOUS_PASSWORD_MISSING"), HttpStatusCodes.BAD_REQUEST);

      return getValidationResult(false);
    },
    [translate, result],
  );

  const _updatePassword = useCallback(
    async (
      request: UpdatePasswordRequest,
      additionalValidation: (request: UpdatePasswordRequest) => OverloadedReturnType<typeof getValidationResult>,
    ) => {
      setIsLoading(true);
      setIsSuccess(false);
      setIsError(false);
      setErrorMessage(undefined);

      let result = _validateUpdatePassword(additionalValidation, request);

      if (result.isError) {
        dispatch(setError({ value: result.errorMessage }));
      } else {
        const response = await sendUpdatePassword(dispatch, request);

        if (!response.isOk())
          result = getValidationResult(true, response.message, response.statusCode as ValueOf<typeof HttpStatusCodes>);
      }

      setIsSuccess(!result.isError);
      setIsError(result.isError);

      if (result.isError) setErrorMessage(result.errorMessage);

      setIsLoading(false);
    },
    [dispatch, _validateUpdatePassword],
  );

  const updatePassword = useCallback(
    async (request: Required<Omit<UpdatePasswordRequest, "token">>) =>
      await _updatePassword(request, validateUpdatePassword),
    [_updatePassword, validateUpdatePassword],
  );

  const updatePasswordWithToken = useCallback(
    async (request: Required<Omit<UpdatePasswordRequest, "previous">>) =>
      await _updatePassword(request, validateUpdatePasswordWithToken),
    [_updatePassword, validateUpdatePasswordWithToken],
  );

  return { updatePassword, updatePasswordWithToken, isLoading, isSuccess, isError, errorMessage };
};

export default useUpdatePassword;
